import { createReference, MedplumClient, parseReference } from "@medplum/core";
import { CareTeam, Communication, Patient, Practitioner, Reference } from "@medplum/fhirtypes";

import { CommunicationCategoryCodes, QuiltedHealthCodeSystems } from "@/fhir/enums/quilted-health";
import { CommunicationInbox } from "@/store/slices/inbox";
import { setAccounts } from "@/utils/accounts";
import { getOrganizationReference, getResourcesFromBundle } from "@/utils/medplum";

import {
  COMMUNICATION_INBOX_CATEGORY,
  COMMUNICATION_THREAD_CATEGORY,
  INBOX_SEARCH_MAX_COUNT,
} from "../constants/messages";
import { createCommunicationInbox } from "../utils/inbox";
import { readMessagePreview } from "../utils/message-content";
import {
  NewMessageToClientFormType,
  NewMessageToProviderFormType,
  NewMessageToTeamFormType,
} from "../zod-schemas/messages";

import { fetchUnreadCountsByThread } from "./chat";

/** What a thread list loads beyond each row's unread count and last message time. */
export interface InboxListOptions {
  /** Loads the text of each thread's last message, for lists that show it under the topic. */
  includeLastMessagePreview?: boolean;
}

/**
 * Lists the inbox threads the current user is a recipient of.
 *
 * @param medplum - the Medplum client.
 * @param practiceId - the practice whose compartment scopes the search.
 * @param clientReference - narrows the list to threads that also include this client, as `Patient/<id>`.
 * @param options - what else each row loads.
 * @returns the threads, with unread counts and the last message time.
 */
export async function fetchCommunicationInboxList(
  medplum: MedplumClient,
  practiceId: string | undefined,
  clientReference?: string,
  options: InboxListOptions = {},
): Promise<CommunicationInbox[]> {
  const query = new URLSearchParams({
    status: "in-progress,completed",
    category: COMMUNICATION_INBOX_CATEGORY,
    recipient: createReference(medplum.getProfile()!).reference!,
    _elements: "recipient,topic,status",
    _sort: "-status",
    _compartment: getOrganizationReference(practiceId),
    _count: String(INBOX_SEARCH_MAX_COUNT),
  });
  if (clientReference) {
    // Repeated `recipient` params are ANDed: only threads that include both the user and the client.
    query.append("recipient", clientReference);
  }

  const communications = await medplum.searchResources("Communication", query);
  return await fetchCommunicationInboxListFromCommunications(
    medplum,
    communications,
    practiceId,
    options,
  );
}

/**
 * Rebuilds the inbox rows of the threads that new messages belong to.
 *
 * @param medplum - the Medplum client.
 * @param communications - the new thread messages.
 * @param practiceId - the practice whose compartment scopes the search.
 * @param options - what else each row loads.
 * @returns a row for each thread the messages are part of.
 */
export async function fetchCommunicationInboxListForSubscription(
  medplum: MedplumClient,
  communications: Communication[],
  practiceId: string | undefined,
  options: InboxListOptions = {},
): Promise<CommunicationInbox[]> {
  const communicationsToFetch = communications
    .map((communication) => {
      const [_, parentCommunicationId] = communication.partOf?.length
        ? parseReference(communication.partOf[0])
        : [];
      return parentCommunicationId;
    })
    .filter(Boolean) as string[];

  const { bundle: parentCommunicationsBundle } = await medplum.searchResources("Communication", {
    _id: communicationsToFetch.join(","),
    _compartment: getOrganizationReference(practiceId),
    _count: INBOX_SEARCH_MAX_COUNT,
  });

  const parentCommunications = getResourcesFromBundle("Communication", parentCommunicationsBundle);
  return await fetchCommunicationInboxListFromCommunications(
    medplum,
    parentCommunications,
    practiceId!,
    options,
  );
}

export async function startNewMessageToClient(
  medplum: MedplumClient,
  practiceId: string | undefined,
  values: NewMessageToClientFormType,
) {
  const subjectReference = createReference(values.client!);

  const communication = getBaseCommunication(medplum, [subjectReference], values.topic);

  const response = await medplum.createResource({
    ...communication,
    subject: subjectReference,
  });

  await setAccounts(medplum, response, [{ reference: getOrganizationReference(practiceId) }]);

  return response;
}

export async function startNewMessageToProvider(
  medplum: MedplumClient,
  practiceId: string | undefined,
  values: NewMessageToProviderFormType,
) {
  const recipientReference = createReference(values.provider!);

  const communication = getBaseCommunication(medplum, [recipientReference], values.topic);

  const response = await medplum.createResource(communication);

  await setAccounts(medplum, response, [{ reference: getOrganizationReference(practiceId) }]);

  return response;
}

export async function startNewMessageToTeam(
  medplum: MedplumClient,
  practiceId: string | undefined,
  values: NewMessageToTeamFormType,
) {
  const profile = medplum.getProfile();
  if (!profile) {
    throw new Error("Profile not found");
  }

  const recipientReferences = values.team?.participant
    ?.map((participant) => participant.member)
    .filter((participant) => {
      const [resourceType, id] = parseReference(participant);
      return resourceType === "Practitioner" && id !== profile.id;
    }) as Reference<Practitioner | Patient>[];

  if (((values.lockClient && values.includeClient) || !values.lockClient) && values.client) {
    const { id, resourceType } = values.client;
    if (resourceType === "Patient" && id !== profile.id) {
      const subjectReference = createReference(values.client);
      recipientReferences.push(subjectReference);
    }
  }

  const communication = getBaseCommunication(
    medplum,
    [createReference(values.team!), ...recipientReferences],
    values.topic,
  );

  const response = await medplum.createResource({
    ...communication,
    subject: values.team?.subject,
  });

  await setAccounts(medplum, response, [{ reference: getOrganizationReference(practiceId) }]);

  return response;
}

/**
 * Builds inbox rows from the thread Communications, one per thread.
 *
 * Each row carries the unread count and the last message time, plus the last message's preview
 * when `options.includeLastMessagePreview` is set.
 */
async function fetchCommunicationInboxListFromCommunications(
  medplum: MedplumClient,
  communications: Communication[],
  practiceId: string | undefined,
  { includeLastMessagePreview = false }: InboxListOptions = {},
): Promise<CommunicationInbox[]> {
  const profile = medplum.getProfile();
  if (!profile) return [];

  const clientAndRecipientPerCommunication = (
    await Promise.all(
      communications.map(async (communication) => {
        try {
          const recipient = await getRecipientResource(medplum, communication.recipient);
          const client = await getClientResource(medplum, communication.recipient);
          return {
            communicationId: communication.id!,
            recipient,
            client,
          };
        } catch (error) {
          console.error(error);
          return undefined;
        }
      }),
    )
  )
    .filter(Boolean)
    .reduce(
      (acc, curr) => {
        acc[curr!.communicationId] = [curr!.recipient, curr!.client];
        return acc;
      },
      {} as Record<string, [Patient | Practitioner | CareTeam | undefined, Patient | undefined]>,
    );

  // The unread counts (a few batched searches) and every thread's last message load together, so
  // the list takes about as long as its slowest request. Promise.all keeps the rows in order.
  const [unreadCounts, rows] = await Promise.all([
    fetchUnreadCountsByThread(medplum, profile, communications),
    Promise.all(
      communications.map(async (communication) => {
        const lastMessage = await medplum.searchOne("Communication", {
          "part-of": createReference(communication).reference,
          category: COMMUNICATION_THREAD_CATEGORY,
          _elements: includeLastMessagePreview ? "sent,payload" : "sent",
          _sort: "-sent",
          _compartment: getOrganizationReference(practiceId),
        });
        return { communication, lastMessage };
      }),
    ),
  ]);

  return rows.reduce<CommunicationInbox[]>((inbox, { communication, lastMessage }) => {
    const [recipient, client] = clientAndRecipientPerCommunication[communication.id!];
    if (!recipient) return inbox;

    inbox.push(
      createCommunicationInbox(communication, {
        topic: communication.topic?.text,
        recipient,
        time: lastMessage?.sent ?? lastMessage?.meta?.lastUpdated,
        unread: unreadCounts.get(createReference(communication).reference) ?? 0,
        client,
        lastMessagePreview:
          includeLastMessagePreview && lastMessage ? readMessagePreview(lastMessage) : undefined,
      }),
    );
    return inbox;
  }, []);
}

async function getRecipientResource(
  medplum: MedplumClient,
  recipients: Communication["recipient"],
): Promise<Patient | Practitioner | CareTeam | undefined> {
  const profile = medplum.getProfile();

  if (!profile) return;

  const validRecipients = recipients?.filter(
    (recipient) => parseReference(recipient)[1] !== profile.id,
  ) as Reference<Patient | Practitioner | CareTeam>[] | undefined;

  const careTeamRecipient = validRecipients?.find(
    (recipient) => parseReference(recipient)[0] === "CareTeam",
  );

  let recipient: Reference<Patient | Practitioner | CareTeam> | undefined;
  if (careTeamRecipient) {
    recipient = careTeamRecipient;
  } else {
    recipient = validRecipients?.[0];
  }

  if (!recipient) return;

  const resource = (await medplum.readReference(recipient)) as Patient | Practitioner | CareTeam;
  return resource;
}

async function getClientResource(
  medplum: MedplumClient,
  recipients: Communication["recipient"],
): Promise<Patient | undefined> {
  const clientReference = recipients?.find(
    (recipient) => parseReference(recipient)[0] === "Patient",
  ) as Reference<Patient> | undefined;

  if (!clientReference) return;
  try {
    return await medplum.readReference(clientReference);
  } catch (error) {
    console.error(error);
    // If client is not found, return undefined
    return;
  }
}

function getBaseCommunication(
  medplum: MedplumClient,
  recipient: Communication["recipient"],
  topic: string,
): Communication {
  const profile = medplum.getProfile();

  if (!profile) {
    throw new Error("Profile not found");
  }

  const profileReference = createReference(profile);

  return {
    resourceType: "Communication",
    recipient: [...(recipient ?? []), profileReference],
    status: "in-progress",
    sender: profileReference,
    category: [
      {
        coding: [
          {
            code: CommunicationCategoryCodes.Inbox,
            system: QuiltedHealthCodeSystems.CommunicationCategories,
          },
        ],
      },
    ],
    topic: {
      text: topic,
    },
  };
}
